<template>
	<div class="line-chart-widget">
		<div v-if="!isMetricAvailable" class="widget-unavailable">
			<span>{{ $t('dashboard_widget_unavailable') }}</span>
		</div>
		<div v-else-if="!chartItem" class="widget-unavailable">
			<span>{{ $t('loading') }}</span>
		</div>
		<div
			v-else
			class="score-chart"
			:style="{ backgroundColor: chartItem.dataset[0].gradientEnd }"
		>
			<div :class="['title', isLastYear ? 'white' : '']">
				<div class="title__top">
					<div>
						{{ chartItem.dataset[0].label }}
						<span class="time-range-hint">{{ rowTitle }}</span>
					</div>
					<button
						v-if="complaintsStripToggleShown"
						type="button"
						class="complaint-strip-toggle"
						:aria-expanded="overviewShowComplaintDevicesInstalled ? 'true' : 'false'"
						:aria-label="$t('overview_complaints_devices_toggle_aria')"
						:title="$t('overview_complaints_devices_toggle_aria')"
						@click.stop="toggleComplaintDevicesInstalled"
					>
						<img
							class="complaint-strip-toggle__finger"
							:class="{ 'is-expanded': overviewShowComplaintDevicesInstalled }"
							:src="fingerClickingIconSrc"
							alt=""
							aria-hidden="true"
						/>
					</button>
				</div>
				<div class="number">
					{{ chartItem.dataset[0].sum }}
					<img
						v-if="!isLastYear"
						:class="chartItem.dataset[0].isTrendUp ? 'up' : 'down'"
						:style="{ backgroundColor: chartItem.dataset[0].trendArrowBackground }"
						:src="require(`@/assets/performance/trend_white.svg`)"
					/>
					<ArrowIcon
						v-else
						:iconColor="chartItem.dataset[0].trendArrowColor"
						:class="['arrow-bold', chartItem.dataset[0].isTrendUp ? 'up' : 'down']"
					/>
				</div>
			</div>
			<div class="score-chart__chart-wrap">
				<Chart
					type="line"
					:options="chartOptions"
					:datasets="chartItem.dataset"
					:labels="chartItem.labels"
					height="100%"
					width="100%"
					style="border-radius:10px;"
				/>
			</div>
			<div
				v-if="complaintsStripShown"
				class="complaint-device-counts-block"
			>
				<div
					class="complaint-device-counts-caption on-dark"
					:title="$t('overview_complaints_devices_caption_hint')"
				>
					{{ $t('overview_complaints_devices_caption') }}
				</div>
				<div class="complaint-device-counts on-dark" dir="ltr">
					<div
						v-for="(n, di) in chartItem.complaintDeviceCounts"
						:key="di"
						class="complaint-device-counts__cell"
						:title="complaintDeviceCellTitle(di, n)"
					>
						<span class="complaint-device-counts__mono-line">
							<span class="complaint-device-counts__month">{{
								chartItem.labels[di] != null ? chartItem.labels[di] : '—'
							}}</span>
							<span class="complaint-device-counts__sep">·</span>
							<span class="complaint-device-counts__value">{{ formatComplaintDeviceCount(n) }}</span>
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { overviewLineChartsOptions } from '../consts/consts'
import utilService from '../../common/services/utilService'
import Chart from '../../common/components/Chart'
import ArrowIcon from '../../common/icons/ArrowIcon'
import { mapGetters } from 'vuex'
import { isMetricAvailable as isMetricAvailableForSite, buildWidgetSiteContext } from './widgetRegistry'

export default {
	name: 'LineChartWidget',
	props: {
		config: { type: Object, required: true },
		widgetData: { type: Object, required: false, default: () => ({}) }
	},
	computed: {
		...mapGetters(['currentFilteringSite', 'overviewShowComplaintDevicesInstalled']),
		metric() {
			return this.config.metric || 'avg_score'
		},
		timeRange() {
			return this.config.timeRange || 'lastMonth'
		},
		isLastYear() {
			return this.timeRange === 'lastYear'
		},
		isMetricAvailable() {
			return isMetricAvailableForSite(
				this.metric,
				buildWidgetSiteContext({
					currentFilteringSite: this.currentFilteringSite,
					currentSiteInfo: null
				})
			)
		},
		chartItem() {
			const byKey = this.widgetData.chartDataByKey
			if (!byKey) return null
			return byKey[`${this.metric}:${this.timeRange}`] || null
		},
		rowTitle() {
			if (!this.isLastYear) return this.$t('last_month')
			const dynamicDates = this.widgetData.dynamicChartDates || 'last_year'
			return this.$te(dynamicDates) ? this.$t(dynamicDates) : dynamicDates
		},
		chartOptions() {
			const dataset = this.chartItem.dataset[0]
			const base = JSON.parse(JSON.stringify(overviewLineChartsOptions))
			base.tooltips = { ...overviewLineChartsOptions.tooltips }
			base.tooltips.callbacks = { ...overviewLineChartsOptions.tooltips.callbacks }
			const { max, min } = utilService.findMaxAndMin(dataset.data)
			const range = max - min
			const padding = Math.max(range * 0.3, 2)
			base.scales.yAxes[0].ticks.max = Math.ceil(max + padding)
			base.scales.yAxes[0].ticks.min = Math.max(0, Math.floor(min - padding))
			return base
		},
		complaintsStripToggleShown() {
			return (
				this.isLastYear &&
				this.metric === 'complaints' &&
				this.chartItem &&
				this.chartItem.complaintDeviceCounts &&
				this.chartItem.complaintDeviceCounts.length > 0
			)
		},
		complaintsStripShown() {
			return this.complaintsStripToggleShown && this.overviewShowComplaintDevicesInstalled
		},
		fingerClickingIconSrc() {
			return require('@/assets/finger_clicking.svg')
		}
	},
	methods: {
		toggleComplaintDevicesInstalled() {
			this.$store.commit(
				'setOverviewShowComplaintDevicesInstalled',
				!this.overviewShowComplaintDevicesInstalled
			)
		},
		formatComplaintDeviceCount(n) {
			if (n === null || n === undefined || Number.isNaN(n)) return '—'
			const num = Number(n)
			if (num >= 1000) return utilService.kFormatter(num)
			if (num >= 100) {
				const k = num / 1000
				return k >= 1 ? '1k' : `${k.toFixed(1).replace(/\.0$/, '')}k`
			}
			return String(num)
		},
		complaintDeviceCellTitle(di, n) {
			const month =
				this.chartItem.labels && this.chartItem.labels[di] != null ? String(this.chartItem.labels[di]) : ''
			const full = n === null || n === undefined || Number.isNaN(n) ? '' : String(Number(n))
			if (month && full) return `${month} — ${full}`
			return full || month || ''
		}
	},
	components: { Chart, ArrowIcon }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.line-chart-widget {
	height: 100%;
	width: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.widget-unavailable {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #848593;
	font-size: 0.875rem;
	text-align: center;
	padding: 10px;
	@include border($grey-border-color);
	border-radius: 10px;
	background: #fafbfc;
}

.score-chart {
	@include border($grey-border-color);
	border-radius: 10px;
	height: 100%;
	width: 100%;
	min-width: 0;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;

	&__chart-wrap {
		flex: 1 1 0;
		min-height: 0;
		width: 100%;
		position: relative;
		overflow: hidden;
	}

	.title {
		flex: 0 0 auto;
		color: #848593;
		height: 50px;
		padding: 10px 10px 0;
		&.white {
			color: $white;
		}
		.title__top {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: 6px;
			margin-bottom: 5px;
			> div:first-child {
				min-width: 0;
			}
			.time-range-hint {
				font-size: 0.7rem;
				opacity: 0.75;
				white-space: nowrap;
			}
		}
		.complaint-strip-toggle {
			flex-shrink: 0;
			padding: 3px 5px;
			margin: -2px -4px 0 0;
			border: none;
			background: transparent;
			cursor: pointer;
			color: inherit;
			line-height: 1;
			border-radius: 4px;
			opacity: 0.88;
			&:hover {
				opacity: 1;
				background: rgba(255, 255, 255, 0.12);
			}
		}
		.complaint-strip-toggle__finger {
			display: block;
			width: 20px;
			height: auto;
			object-fit: contain;
			transition: transform 0.2s ease, opacity 0.2s ease;
			transform: rotate(-6deg);
			opacity: 0.92;
			&.is-expanded {
				transform: rotate(2deg) scale(0.96);
				opacity: 1;
			}
		}
		&.white .complaint-strip-toggle__finger {
			filter: brightness(0) invert(1);
		}
		.number {
			display: flex;
			justify-content: space-between;
			font-weight: $font-weight-bold;
			.arrow-bold {
				background-color: $white;
				border-radius: 50%;
				height: 20px;
				width: 20px;
				&.down {
					transform: rotate(180deg);
				}
			}
			img {
				height: 20px;
				border-radius: 50%;
				&.up {
					transform: rotate(180deg);
				}
			}
		}
	}
}

.complaint-device-counts-block {
	flex: 0 0 auto;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	padding: 3px 2px 2px;
	box-sizing: border-box;
	border-top: 1px solid rgba(255, 255, 255, 0.14);
}

.complaint-device-counts-caption {
	font-size: 9px;
	font-weight: $font-weight-semi-bold;
	line-height: 1.1;
	color: $black-blue;
	opacity: 0.88;
	text-align: center;
	margin-bottom: 1px;
	padding: 0 2px;
	cursor: help;
	&.on-dark {
		color: $white;
		opacity: 0.9;
	}
}

.complaint-device-counts {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	gap: 3px 6px;
	width: 100%;
	min-width: 0;
	padding: 0 0 4px;
	box-sizing: border-box;
	font-size: 9px;
	font-weight: $font-weight-semi-bold;
	font-variant-numeric: tabular-nums;
	color: $black-blue;
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;
	touch-action: pan-x;
	overscroll-behavior-x: contain;
	scrollbar-width: thin;
	&::-webkit-scrollbar {
		height: 8px;
	}
	&::-webkit-scrollbar-track {
		background: rgba(0, 0, 0, 0.12);
		border-radius: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.4);
		border-radius: 4px;
	}
	&__cell {
		flex: 0 0 auto;
		max-width: 5.5rem;
		padding: 0 2px;
		line-height: 1.1;
	}
	&__mono-line {
		display: inline-flex;
		flex-direction: row;
		align-items: baseline;
		justify-content: center;
		gap: 2px;
		max-width: 100%;
		white-space: nowrap;
	}
	&__month {
		flex: 0 1 auto;
		min-width: 0;
		font-size: 9px;
		font-weight: $font-weight-semi-bold;
		opacity: 0.85;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&__sep {
		flex: 0 0 auto;
		opacity: 0.55;
		font-size: 7px;
		padding: 0 1px;
	}
	&__value {
		flex: 0 0 auto;
		font-size: 9px;
		font-weight: $font-weight-bold;
	}
	&.on-dark {
		color: $white;
	}
}
</style>
